import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { HeartPulse, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { NextSteps, ComponentBars, scoreColor, rgb, TICK, GRID, TOOLTIP } from "@/components/BrandBits";

export default function BrandHealth() {
  const { ready, scopeParam, ClientPicker } = useClientScope();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState(null);

  const load = useCallback(() => {
    if (!ready) return;
    setLoading(true);
    const q = scopeParam ? `&${scopeParam}` : "";
    api.get(`/brand/health?days=${days}${q}`)
      .then(({ data }) => setData(data))
      .catch((e) => { setData(null); toast.error(formatApiErrorDetail(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
    api.get(`/brand/actions${scopeParam ? `?${scopeParam}` : ""}`).then(({ data }) => setActions(data)).catch(() => setActions([]));
  }, [ready, scopeParam, days]);

  useEffect(() => { load(); }, [load]);

  const delta = data?.delta_7d;
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const sent = data?.sentiment;
  const sentTotal = sent ? sent.positive + sent.neutral + sent.negative : 0;

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6" data-testid="brand-health-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Reputation at a glance</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Brand Health</h1>
          </div>
          <div className="flex items-center gap-3">
            <ClientPicker />
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="brand-days"
              className="px-3 py-1.5 rounded-lg border border-input bg-card text-sm font-medium text-slate-700">
              {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
          </div>
        </div>

        {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

        {!loading && data && data.score == null && (
          <div className="bg-card rounded-lg border border-slate-100 p-10 text-center text-slate-500" data-testid="brand-empty">
            <HeartPulse className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No comments in this window yet. The score appears once comments are ingested.
          </div>
        )}

        {!loading && data && data.score != null && (
          <>
            {actions && actions.length > 0 && (
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm" data-testid="brand-next-steps">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Recommended next steps</p>
                <NextSteps actions={actions} qs={scopeParam ? `?${scopeParam}` : ""} />
              </div>
            )}
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm" data-testid="brand-score-card">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Brand health score</p>
                <div className="flex items-end gap-3 mt-2">
                  <span className="font-heading text-6xl font-medium" style={{ color: scoreColor(data.score) }} data-testid="brand-score">{data.score}</span>
                  <span className="text-slate-400 mb-2">/ 100</span>
                </div>
                <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-500" data-testid="brand-delta">
                  <DeltaIcon className="w-4 h-4" />
                  {delta == null ? "Not enough history for a 7-day comparison" : `${delta > 0 ? "+" : ""}${delta} vs the previous 7 days`}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  {data.comment_count} comments analysed, {Math.round((data.hostile_ratio || 0) * 100)}% hostile
                </p>
              </div>
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm lg:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">What the score is made of</p>
                <ComponentBars components={data.components} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Score trend</p>
              <div className="h-64" data-testid="brand-trend">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                    <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={TICK} />
                    <YAxis domain={[0, 100]} tick={TICK} />
                    <Tooltip {...TOOLTIP} />
                    <Line type="monotone" dataKey="score" name="Score" stroke={rgb("accent", 600)} strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Sentiment mix</p>
                <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                  {[["positive", rgb("accent", 600)], ["neutral", rgb("stone", 400)], ["negative", rgb("oxblood", 500)]].map(([k, col]) => (
                    <div key={k} style={{ width: `${sentTotal ? (sent[k] / sentTotal) * 100 : 0}%`, background: col }} />
                  ))}
                </div>
                <div className="flex gap-5 mt-3 text-sm text-slate-600">
                  {["positive", "neutral", "negative"].map((k) => <span key={k}><span className="font-mono">{sent[k]}</span> {k}</span>)}
                </div>
              </div>
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">Top themes</p>
                {data.themes.length === 0 ? <p className="text-sm text-slate-400">No recurring themes yet.</p> : (
                  <ul className="space-y-2" data-testid="brand-themes">
                    {data.themes.map((t) => (
                      <li key={t.theme} className="flex justify-between text-sm">
                        <span className="text-slate-700 capitalize">{t.theme}</span>
                        <span className="font-mono text-slate-500">{t.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {Object.keys(data.emotions || {}).length > 0 && (
              <div className="bg-card rounded-lg border border-slate-100 p-6 shadow-sm" data-testid="brand-emotions">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-4">What people are feeling</p>
                <ul className="space-y-2">
                  {Object.entries(data.emotions).map(([k, v]) => (
                    <li key={k} className="flex items-center gap-3 text-sm">
                      <span className="w-20 text-slate-700 capitalize">{k}</span>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.round(v * 100)}%`, background: k === "joy" ? rgb("accent", 600) : k === "neutral" ? rgb("stone", 400) : rgb("oxblood", 500) }} />
                      </div>
                      <span className="font-mono text-slate-500 w-10 text-right">{Math.round(v * 100)}%</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-400 mt-3">{data.ai_read} of {data.comment_count} comments read by AI for sarcasm and coded wording.</p>
              </div>
            )}
            <p className="text-sm text-slate-500">
              Want the detail behind the number? <Link to="/posts" className="underline">See post report cards</Link>.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
